import { createSlice } from "@reduxjs/toolkit";
import jwt_decode from "jwt-decode";
import axiosInstance from "../axiosInstance/AxiosInstance";
import { toastClick } from "../../composants/toast/CustomToast";
import * as Utils from "../../composants/utils/utils";

const MAX_REFRESH_COUNT = 10;

function isValidJSON(str) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

const authSliceUser = createSlice({
    name: 'authUser',
    initialState: {
        permissions: sessionStorage.getItem('permissionsAdmin'),
        accessToken: sessionStorage.getItem('accessTokenAdmin'),
        refreshToken: sessionStorage.getItem('refreshTokenAdmin'),
        refreshExpiresIn: sessionStorage.getItem('refreshExpiresIn'),
        expiresIn: sessionStorage.getItem('expiresIn'),
        tokensubject: sessionStorage.getItem('subAdmin'),
        droits: isValidJSON(sessionStorage.getItem('rolesAdmin')) ? JSON.parse(sessionStorage.getItem('rolesAdmin')) : [],
        user: isValidJSON(sessionStorage.getItem('userAdmin')) ? JSON.parse(sessionStorage.getItem('userAdmin')) : null,
        isAuth: sessionStorage.getItem('isAuthAdmin'),
        refreshCount: Number(sessionStorage.getItem('refreshCount') || 0),
        showReloginModal: false,
    },

    reducers: {
        setExpiresIn: (state, action) => {
            state.expiresIn = sessionStorage.getItem('expiresIn')
        },
        setRefreshExpiresIn: (state, action) => {
            state.refreshExpiresIn = sessionStorage.getItem('refreshExpiresIn')
        },
        setAccessToken: (state, action) => {
            state.accessToken = sessionStorage.getItem('accessTokenAdmin')
        },
        setRefreshToken: (state, action) => {
            state.refreshToken = sessionStorage.getItem('refreshTokenAdmin')
        },
        setSubject: (state, action) => {
            state.tokensubject = sessionStorage.getItem('subAdmin')
        },
        setPermissions: (state, action) => {
            state.permissions = sessionStorage.getItem('permissionsAdmin')
        },
        setDroits: (state, action) => {
            state.droits = isValidJSON(sessionStorage.getItem('rolesAdmin')) ? JSON.parse(sessionStorage.getItem('rolesAdmin')) : [];
        },
        setUser: (state, action) => {
            state.user = isValidJSON(sessionStorage.getItem('userAdmin')) ? JSON.parse(sessionStorage.getItem('userAdmin')) : null;
            state.isAuth = state.user ? state.user.isFirstConnexion : false;
        },
        logOut: (state, action) => {
            sessionStorage.clear();
            state.droits = [];
            state.tokensubject = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.expiresIn = null;
            state.refreshExpiresIn = null;
            state.user = null;
            state.refreshCount = 0;
            state.showReloginModal = false;
            state.isAuth = false;
        },
        getToken: (state) => {
            return state.accessToken;
        },
        incrementRefreshCount: (state) => {
            state.refreshCount += 1;
            sessionStorage.setItem('refreshCount', state.refreshCount);
        },
        resetRefreshCount: (state) => {
            state.refreshCount = 0;
            sessionStorage.setItem('refreshCount', '0');
        },
        setShowReloginModal: (state, action) => {
            state.showReloginModal = action.payload;
        },
        setNewTokens: (state, action) => {
            const { accessToken, refreshToken, expiresIn, refreshExpiresIn } = action.payload;
            state.accessToken = accessToken;
            state.refreshToken = refreshToken;
            state.expiresIn = expiresIn;
            state.refreshExpiresIn = refreshExpiresIn;
        },
    }
})


export const {
    setRefreshToken, setAccessToken, setExpiresIn, setRefreshExpiresIn,
    setDroits, logOut, setSubject, setUser, getToken, setPermissions,
    incrementRefreshCount, resetRefreshCount, setShowReloginModal, setNewTokens
} = authSliceUser.actions;
// ─── Token Refresh Thunk ─────────────────────────────────────────────────────
export const refreshUserToken = (refreshToken) => async (dispatch, getState) => {
    const { refreshCount } = getState().authUser;
    if (refreshCount >= MAX_REFRESH_COUNT) {
        // Trop de rafraîchissements : forcer la reconnexion
        dispatch(setShowReloginModal(true));
        return false;
    }
    try {
        const response = await axiosInstance().post(
            `api/auth/refresh?refresh=${encodeURIComponent(refreshToken)}`
        );
        const { access_token, refresh_token, expires_in, refresh_expires_in } = response.data;
        // Mettre à jour sessionStorage
        sessionStorage.setItem('accessTokenAdmin', access_token);
        sessionStorage.setItem('refreshTokenAdmin', refresh_token);
        sessionStorage.setItem('expiresIn', expires_in);
        sessionStorage.setItem('refreshExpiresIn', refresh_expires_in);
        // Mettre à jour Redux
        dispatch(setNewTokens({
            accessToken: access_token,
            refreshToken: refresh_token,
            expiresIn: expires_in,
            refreshExpiresIn: refresh_expires_in,
        }));
        dispatch(incrementRefreshCount());
        return access_token;
    } catch (error) {
        // Refresh échoué → forcer la reconnexion
        dispatch(setShowReloginModal(true));
        return false;
    }
};

// ─── Login Thunk ──────────────────────────────────────────────────────────────
export const loginUserClient = (identifier, password, setLoading) => async dispatch => {
    await axiosInstance().post("public/api/auth/login", {
        login: identifier,
        password: password
    }).then(response => {
        const accessToken = response.data.access_token;
        const refreshToken = response.data.refresh_token;
        const permissions = response.data.permissions;
        const expiresIn = response.data.expires_in;
        const refreshExpiresIn = response.data.refresh_expires_in;
        const user = response.data.user;
        let decodedToken;
        try {
            decodedToken = jwt_decode(accessToken);
            // Vérifier si le token est expiré
            const currentTime = Math.floor(Date.now() / 1000);
            if (decodedToken.exp < currentTime) {
                toastClick("Token expiré, veuillez vous reconnecter", "error");
                setLoading(false);
                return;
            }
        } catch (error) {
            toastClick("Erreur lors du décodage du token", "error");
            setLoading(false);
            return;
        }

        sessionStorage.setItem('accessTokenAdmin', accessToken);
        sessionStorage.setItem('refreshTokenAdmin', refreshToken);
        sessionStorage.setItem('rolesAdmin', JSON.stringify(permissions));
        sessionStorage.setItem('subAdmin', decodedToken.sub);
        sessionStorage.setItem('userAdmin', JSON.stringify(user));
        sessionStorage.setItem('isAuthAdmin', user.isFirstConnexion);
        sessionStorage.setItem('expiresIn', expiresIn);
        sessionStorage.setItem('refreshExpiresIn', refreshExpiresIn);
        if (user.isFirstConnexion)
            sessionStorage.setItem('passwordAdmin', password)
        dispatch(setAccessToken(sessionStorage.getItem('accessTokenAdmin')));
        dispatch(setRefreshToken(sessionStorage.getItem('refreshTokenAdmin')));
        dispatch(setDroits(sessionStorage.getItem('rolesAdmin')));
        dispatch(setSubject(sessionStorage.getItem('subAdmin')));
        dispatch(setUser(JSON.parse(sessionStorage.getItem('userAdmin'))));
        dispatch(setExpiresIn(sessionStorage.getItem('expiresIn')));
        dispatch(setRefreshExpiresIn(sessionStorage.getItem('refreshExpiresIn')));
        // Réinitialiser le compteur de refresh après une connexion réussie
        dispatch(resetRefreshCount());
        dispatch(setShowReloginModal(false));
        const roles = JSON.parse(sessionStorage.getItem('rolesAdmin'));
        if (roles.includes(Utils.PERMISSIONS.SEE_DASHBOARD.value)) {
            toastClick("Welcome", "success");
            window.location.href = Utils.DASHBOARD;
        } else if (roles.includes(Utils.PERMISSIONS.GET_ALERT.value)) {
            toastClick("Welcome", "success");
            window.location.href = Utils.ALERT;
        } else {
            toastClick("Vous n'avez pas les autorisations requises", "error");
            window.location.href = "/";
        }
    }).catch(error => {
        setLoading(false);
        toastClick("Login / Ou Mot de passe Invalide", "error");
    });
};

export const logOutUser = () => async dispatch => {
    dispatch(logOut(""));
};
export const setImage = (data) => dispatch => {
    sessionStorage.setItem('userAdmin', JSON.stringify(data));
    dispatch(setUser(data));
};
export const setInfos = (nom, prenom, user) => async dispatch => {
    user.nom = nom;
    user.prenom = prenom;
    sessionStorage.setItem('userAdmin', JSON.stringify(user));
};

export default authSliceUser.reducer;
